import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { DomainStatItem, DomainStatsTotals, DomainType, ReportingComparison, VendorGroupBy } from '../../types/reporting';
import { rehydrateDomainStatItem } from '../../utils/reportingBySource';

interface DomainStatsRequest {
    domain: DomainType;
    startDate: string;
    endDate: string;
    locationId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
    excludeEvs?: boolean;
    groupBy?: VendorGroupBy;
    /** When false, API omits bySource (smaller overview all-rows payloads). */
    includeBySource?: boolean;
}

interface RawDomainStatsResponse {
    items: RawDomainStatItem[];
    totals: RawTotals;
    domain: DomainType;
}

// Raw item from backend - may have different field names
interface RawDomainStatItem {
    // ID fields - backend uses various names
    id?: string;
    vendorId?: string;
    locationId?: string;
    insuranceId?: string;
    oemId?: string;
    makeId?: string;

    // Name fields - backend uses various names
    name?: string;
    vendorName?: string;
    locationName?: string;
    insuranceName?: string;
    oemName?: string;
    makeName?: string;

    // Metrics
    orderCount?: number;
    orderIds?: string[];
    orderVolume?: number;
    orderVolumeWoPM?: number;
    orderCost?: number;
    partsOrdered?: number;
    grossProfit?: number;
    grossProfitWoPM?: number;

    // Gross margin - backend may use different names
    grossMarginPct?: number;
    avgGrossMarginPct?: number;
    medianGrossMarginPct?: number;

    // Other metrics that may or may not exist
    missedOpportunity?: number;
    moEligibleOrderCount?: number;
    avgMissedOpportunity?: number;
    medianMissedOpportunity?: number;
    avgListPrice?: number;
    avgListPriceWoPM?: number;
    avgOrderSize?: number;
    medianListPrice?: number;
    medianListPriceWoPM?: number;
    medianOrderSize?: number;
    avgGrossMarginPctWoPM?: number;
    medianGrossMarginPctWoPM?: number;
    p90GrossMarginPct?: number;
    grossMarginLossFromPM?: number;
    partListPricePctOfOE?: number;
    totalFinalListPriceForOEM?: number;
    totalOriginalListPriceForOEM?: number;
    pctAutoOrdered?: number;
    pctAutoOrderEnabled?: number;
    pctPartsFlagged?: number;
    pctOfTotal?: number;
    agreedDiscount?: number | null;
    missedOpportunityEligible?: boolean;
    avgPartValue?: number;
    medianPartValue?: number;
    totalQuotes?: number;
    avgQuotesPerOrder?: number;
    partsWithEtaChange?: number;
    avgEtaChangeDays?: number;
    medianEtaChangeDays?: number;
    totalCostVariance?: number;
    avgCostVariance?: number;
    medianCostVariance?: number;
    avgDeliveryVarianceDays?: number;
    medianDeliveryVarianceDays?: number;
    deliveryVariancePartCount?: number;
    pctPartsInvoiced?: number;
    quoteOpportunities?: number;
    quotesSubmitted?: number;
    quoteRate?: number;
    totalReturnCount?: number;
    totalReturnValue?: number;
    totalReturnPct?: number;
    pctOemVolume?: number;
    pctOemCount?: number;
    pctLkqVolume?: number;
    pctLkqCount?: number;
    pctAftermarketVolume?: number;
    pctAftermarketCount?: number;
}

interface RawTotals {
    orderCount?: number;
    orderVolume?: number;
    orderVolumeWoPM?: number;
    orderCost?: number;
    partsOrdered?: number;
    grossProfit?: number;
    grossProfitWoPM?: number;
    grossMarginPct?: number;
    avgGrossMarginPct?: number;
    medianGrossMarginPct?: number;
    missedOpportunity?: number;
    moEligibleOrderCount?: number;
    avgMissedOpportunity?: number;
    medianMissedOpportunity?: number;
    partListPricePctOfOE?: number;
    totalFinalListPriceForOEM?: number;
    totalOriginalListPriceForOEM?: number;
    avgListPrice?: number;
    avgListPriceWoPM?: number;
    avgOrderSize?: number;
    medianListPrice?: number;
    medianListPriceWoPM?: number;
    medianOrderSize?: number;
    avgGrossMarginPctWoPM?: number;
    medianGrossMarginPctWoPM?: number;
    p90GrossMarginPct?: number;
    grossMarginLossFromPM?: number;
    pctAutoOrdered?: number;
    pctAutoOrderEnabled?: number;
    avgPartValue?: number;
    medianPartValue?: number;
    totalQuotes?: number;
    avgQuotesPerOrder?: number;
    partsWithEtaChange?: number;
    avgEtaChangeDays?: number;
    medianEtaChangeDays?: number;
    totalCostVariance?: number;
    avgCostVariance?: number;
    medianCostVariance?: number;
    avgDeliveryVarianceDays?: number;
    medianDeliveryVarianceDays?: number;
    deliveryVariancePartCount?: number;
    pctPartsInvoiced?: number;
    totalReturnCount?: number;
    totalReturnValue?: number;
    totalReturnPct?: number;
    pctOemVolume?: number;
    pctOemCount?: number;
    pctLkqVolume?: number;
    pctLkqCount?: number;
    pctAftermarketVolume?: number;
    pctAftermarketCount?: number;
}

// Transform raw item to expected DomainStatItem structure
const transformItem = (raw: RawDomainStatItem & { prior?: any; change?: any; bySource?: Record<string, DomainStatItem>; }, domain: DomainType): DomainStatItem => {
    // Get ID based on domain
    const id = raw.id ||
        raw.vendorId ||
        raw.locationId ||
        raw.insuranceId ||
        raw.oemId ||
        raw.makeId ||
        'unknown';

    // Get name based on domain - try multiple field names
    const name = raw.name ||
        raw.vendorName ||
        raw.locationName ||
        raw.insuranceName ||
        raw.oemName ||
        raw.makeName ||
        'Unknown';

    // Get gross margin - backend might use different field names
    const avgGrossMarginPct = raw.avgGrossMarginPct ?? raw.grossMarginPct ?? 0;

    const result: DomainStatItem = {
        id,
        name,
        orderCount: raw.orderCount ?? 0,
        orderIds: raw.orderIds,
        orderVolume: raw.orderVolume ?? 0,
        orderVolumeWoPM: raw.orderVolumeWoPM ?? 0,
        orderCost: raw.orderCost ?? 0,
        partsOrdered: raw.partsOrdered ?? 0,
        grossProfit: raw.grossProfit ?? 0,
        grossProfitWoPM: raw.grossProfitWoPM ?? 0,
        missedOpportunity: raw.missedOpportunity ?? 0,
        moEligibleOrderCount: raw.moEligibleOrderCount ?? raw.orderCount ?? 0,
        avgListPrice: raw.avgListPrice ?? 0,
        avgListPriceWoPM: raw.avgListPriceWoPM ?? 0,
        avgOrderSize: raw.avgOrderSize ?? 0,
        avgGrossMarginPct,
        avgGrossMarginPctWoPM: raw.avgGrossMarginPctWoPM ?? 0,
        medianListPrice: raw.medianListPrice ?? 0,
        medianListPriceWoPM: raw.medianListPriceWoPM ?? 0,
        medianOrderSize: raw.medianOrderSize ?? 0,
        medianGrossMarginPct: raw.medianGrossMarginPct ?? 0,
        medianGrossMarginPctWoPM: raw.medianGrossMarginPctWoPM ?? 0,
        p90GrossMarginPct: raw.p90GrossMarginPct ?? 0,
        grossMarginLossFromPM: raw.grossMarginLossFromPM ?? 0,
        avgMissedOpportunity: raw.avgMissedOpportunity ?? 0,
        medianMissedOpportunity: raw.medianMissedOpportunity ?? 0,
        partListPricePctOfOE: raw.partListPricePctOfOE ?? 0,
        totalFinalListPriceForOEM: raw.totalFinalListPriceForOEM ?? 0,
        totalOriginalListPriceForOEM: raw.totalOriginalListPriceForOEM ?? 0,
        pctAutoOrdered: raw.pctAutoOrdered ?? 0,
        pctAutoOrderEnabled: raw.pctAutoOrderEnabled ?? 0,
        pctPartsFlagged: raw.pctPartsFlagged ?? 0,
        agreedDiscount: raw.agreedDiscount ?? null,
        avgPartValue: raw.avgPartValue ?? 0,
        medianPartValue: raw.medianPartValue ?? 0,
        totalQuotes: raw.totalQuotes ?? 0,
        avgQuotesPerOrder: raw.avgQuotesPerOrder ?? 0,
        partsWithEtaChange: raw.partsWithEtaChange ?? 0,
        avgEtaChangeDays: raw.avgEtaChangeDays ?? 0,
        medianEtaChangeDays: raw.medianEtaChangeDays ?? 0,
        totalCostVariance: raw.totalCostVariance ?? 0,
        avgCostVariance: raw.avgCostVariance ?? 0,
        medianCostVariance: raw.medianCostVariance ?? 0,
        avgDeliveryVarianceDays: raw.avgDeliveryVarianceDays ?? 0,
        medianDeliveryVarianceDays: raw.medianDeliveryVarianceDays ?? 0,
        deliveryVariancePartCount: raw.deliveryVariancePartCount ?? 0,
        pctPartsInvoiced: raw.pctPartsInvoiced ?? 0,
        totalReturnCount: raw.totalReturnCount ?? 0,
        totalReturnValue: raw.totalReturnValue ?? 0,
        totalReturnPct: raw.totalReturnPct ?? 0,
        pctOemVolume: raw.pctOemVolume ?? 0,
        pctOemCount: raw.pctOemCount ?? 0,
        pctLkqVolume: raw.pctLkqVolume ?? 0,
        pctLkqCount: raw.pctLkqCount ?? 0,
        pctAftermarketVolume: raw.pctAftermarketVolume ?? 0,
        pctAftermarketCount: raw.pctAftermarketCount ?? 0,
    };

    if (raw.missedOpportunityEligible !== undefined) {
        result.missedOpportunityEligible = raw.missedOpportunityEligible;
    }

    if (raw.quoteOpportunities !== undefined) {
        result.quoteOpportunities = raw.quoteOpportunities;
    }
    if (raw.quotesSubmitted !== undefined) {
        result.quotesSubmitted = raw.quotesSubmitted;
    }
    if (raw.quoteRate !== undefined) {
        result.quoteRate = raw.quoteRate;
    }

    // Preserve comparison data if present
    if (raw.prior) {
        result.prior = raw.prior;
    }
    if (raw.change) {
        result.change = raw.change;
    }

    // Preserve per-marketplace breakdown (location/vendor/oem). Single-source
    // entries arrive without metrics and inherit them from the parent row.
    if (raw.bySource) {
        result.bySource = raw.bySource;
    }

    return rehydrateDomainStatItem(result);
};

// Transform raw totals to expected structure
const transformTotals = (raw: RawTotals): DomainStatsTotals => {
    const avgGrossMarginPct = raw.avgGrossMarginPct ?? raw.grossMarginPct ?? 0;

    return {
        orderCount: raw.orderCount ?? 0,
        orderVolume: raw.orderVolume ?? 0,
        orderVolumeWoPM: raw.orderVolumeWoPM ?? 0,
        orderCost: raw.orderCost ?? 0,
        partsOrdered: raw.partsOrdered ?? 0,
        grossProfit: raw.grossProfit ?? 0,
        grossProfitWoPM: raw.grossProfitWoPM ?? 0,
        missedOpportunity: raw.missedOpportunity ?? 0,
        moEligibleOrderCount: raw.moEligibleOrderCount ?? raw.orderCount ?? 0,
        avgMissedOpportunity: raw.avgMissedOpportunity ?? 0,
        medianMissedOpportunity: raw.medianMissedOpportunity ?? 0,
        partListPricePctOfOE: raw.partListPricePctOfOE ?? 0,
        avgListPrice: raw.avgListPrice ?? 0,
        avgListPriceWoPM: raw.avgListPriceWoPM ?? 0,
        avgOrderSize: raw.avgOrderSize ?? 0,
        avgGrossMarginPct,
        avgGrossMarginPctWoPM: raw.avgGrossMarginPctWoPM ?? 0,
        medianListPrice: raw.medianListPrice ?? 0,
        medianListPriceWoPM: raw.medianListPriceWoPM ?? 0,
        medianOrderSize: raw.medianOrderSize ?? 0,
        medianGrossMarginPct: raw.medianGrossMarginPct ?? 0,
        medianGrossMarginPctWoPM: raw.medianGrossMarginPctWoPM ?? 0,
        p90GrossMarginPct: raw.p90GrossMarginPct ?? 0,
        grossMarginLossFromPM: raw.grossMarginLossFromPM ?? 0,
        pctAutoOrdered: raw.pctAutoOrdered ?? 0,
        pctAutoOrderEnabled: raw.pctAutoOrderEnabled ?? 0,
        avgPartValue: raw.avgPartValue ?? 0,
        medianPartValue: raw.medianPartValue ?? 0,
        totalQuotes: raw.totalQuotes ?? 0,
        avgQuotesPerOrder: raw.avgQuotesPerOrder ?? 0,
        partsWithEtaChange: raw.partsWithEtaChange ?? 0,
        avgEtaChangeDays: raw.avgEtaChangeDays ?? 0,
        medianEtaChangeDays: raw.medianEtaChangeDays ?? 0,
        totalCostVariance: raw.totalCostVariance ?? 0,
        avgCostVariance: raw.avgCostVariance ?? 0,
        medianCostVariance: raw.medianCostVariance ?? 0,
        avgDeliveryVarianceDays: raw.avgDeliveryVarianceDays ?? 0,
        medianDeliveryVarianceDays: raw.medianDeliveryVarianceDays ?? 0,
        deliveryVariancePartCount: raw.deliveryVariancePartCount ?? 0,
        pctPartsInvoiced: raw.pctPartsInvoiced ?? 0,
        totalReturnCount: raw.totalReturnCount ?? 0,
        totalReturnValue: raw.totalReturnValue ?? 0,
        totalReturnPct: raw.totalReturnPct ?? 0,
        pctOemVolume: raw.pctOemVolume ?? 0,
        pctOemCount: raw.pctOemCount ?? 0,
        pctLkqVolume: raw.pctLkqVolume ?? 0,
        pctLkqCount: raw.pctLkqCount ?? 0,
        pctAftermarketVolume: raw.pctAftermarketVolume ?? 0,
        pctAftermarketCount: raw.pctAftermarketCount ?? 0,
    };
};

export const getDomainStats = async (
    sessionId: string,
    input: DomainStatsRequest
): Promise<{ items: DomainStatItem[]; totals: DomainStatsTotals; domain: DomainType; }> => {
    const { data } = await axios.post<RawDomainStatsResponse>(
        `${API_URL}/reporting/domain/stats`,
        input,
        { headers: { Authorization: sessionId } }
    );

    // Transform the response
    const items = (data.items || []).map(item => transformItem(item, input.domain));
    const totals = transformTotals(data.totals || {});

    return {
        items,
        totals,
        domain: data.domain || input.domain
    };
};

export type DomainStatsHookOptions = {
    locationId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    preset?: string;
    comparison?: ReportingComparison;
    excludeEvs?: boolean;
    groupBy?: VendorGroupBy;
    includeBySource?: boolean;
    enabled?: boolean;
};

const DOMAIN_STATS_STALE_TIME_MS = 1000 * 60 * 5;

/** Shared React Query config for domain stats (use + prefetch). */
export const getDomainStatsQueryConfig = (
    sessionId: string,
    domain: DomainType,
    startDate: string,
    endDate: string,
    options?: Omit<DomainStatsHookOptions, 'enabled'>,
) => {
    const { comparison, excludeEvs, groupBy, ...restOptions } = options || {};

    return {
        queryKey: ['reporting', 'domain', domain, startDate, endDate, options],
        queryFn: () => getDomainStats(sessionId, {
            domain,
            startDate,
            endDate,
            ...restOptions,
            comparison,
            includeComparison: !!comparison,
            excludeEvs,
            groupBy,
        }),
        staleTime: DOMAIN_STATS_STALE_TIME_MS,
    };
};

export const useDomainStats = (
    domain: DomainType,
    startDate: string,
    endDate: string,
    options?: DomainStatsHookOptions,
) => {
    const sessionContext = useContext(SessionContext);
    const { enabled, ...queryOptions } = options || {};

    return useQuery({
        ...getDomainStatsQueryConfig(
            sessionContext?.sessionId ?? '',
            domain,
            startDate,
            endDate,
            queryOptions,
        ),
        enabled: !!sessionContext?.sessionId && !!startDate && !!endDate && (enabled ?? true),
        placeholderData: (previousData) => previousData,
    });
};

